(() => {
  if (window.SilverBulletEvents) return;

  const EVENT_FIELDS = {
    'silver_bullet:fitment_started': ['source'],
    'silver_bullet:fitment_match_found': ['source', 'fitment_key', 'product_handle', 'variant_id'],
    'silver_bullet:fitment_no_match': ['source', 'year', 'reason', 'make_slug', 'model_slug', 'bed_length_slug'],
    'silver_bullet:vehicle_intake_completed': [
      'product_handle',
      'variant_id',
      'fitment_key',
      'has_accessories',
      'has_other_condition',
    ],
    'silver_bullet:model_request_submitted': ['source', 'page_type'],
    'silver_bullet:shipping_signup_submitted': ['source', 'page_type'],
  };

  const cleanValue = (value) => {
    if (typeof value === 'string') return value.slice(0, 120);
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'boolean') return value;
    return undefined;
  };

  const publish = (eventName, payload = {}) => {
    const allowedFields = EVENT_FIELDS[eventName];
    if (!allowedFields) return false;

    const eventData = {};
    allowedFields.forEach((field) => {
      const value = cleanValue(payload[field]);
      if (value !== undefined) eventData[field] = value;
    });

    document.dispatchEvent(new CustomEvent(eventName, { detail: eventData }));

    try {
      const result = window.Shopify?.analytics?.publish?.(eventName, eventData);
      result?.catch?.(() => {
        // Customer Events may be unavailable when no web pixel is configured.
      });
    } catch (error) {
      // Analytics must never interrupt fitment, forms, cart, or checkout.
    }

    return true;
  };

  const setFeedbackVisibility = (node, visible) => {
    node.hidden = !visible;
    node.classList.toggle('hidden', !visible);
  };

  const getFragmentCustomerForm = () => {
    if (!window.location.hash) return null;

    try {
      const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target instanceof HTMLFormElement && target.classList.contains('sb-customer-form')) return target;
    } catch (error) {
      // A malformed fragment must not interrupt form feedback.
    }

    return null;
  };

  const reconcileCustomerFormFeedback = () => {
    const forms = Array.from(document.querySelectorAll('form.sb-customer-form'));
    const fragmentForm = getFragmentCustomerForm();
    const formsWithServerFeedback = forms.filter(
      (form) =>
        form.querySelector('[data-sb-customer-posted-success]') ||
        form.querySelector('[data-sb-customer-posted-error]')
    );
    const fallbackForm = !fragmentForm && formsWithServerFeedback.length === 1 ? formsWithServerFeedback[0] : null;

    forms.forEach((form) => {
      const hasPostedSuccess = Boolean(form.querySelector('[data-sb-customer-posted-success]'));
      const hasPostedError = Boolean(form.querySelector('[data-sb-customer-posted-error]'));
      const hasServerFeedback = hasPostedSuccess || hasPostedError;

      if (!hasServerFeedback) {
        delete form.dataset.sbFeedbackTarget;
        return;
      }

      const isFeedbackTarget = form === fragmentForm || form === fallbackForm;
      form.dataset.sbFeedbackTarget = isFeedbackTarget ? 'true' : 'false';
      form.querySelectorAll('[data-sb-customer-success]').forEach((node) => {
        setFeedbackVisibility(node, isFeedbackTarget && hasPostedSuccess);
      });
      form.querySelectorAll('[data-sb-customer-controls]').forEach((node) => {
        setFeedbackVisibility(node, !(isFeedbackTarget && hasPostedSuccess));
      });
      form.querySelectorAll('[data-sb-customer-error]').forEach((node) => {
        setFeedbackVisibility(node, isFeedbackTarget && hasPostedError);
      });

      if (isFeedbackTarget) return;

      form.querySelectorAll('[aria-invalid], [aria-describedby], [autofocus], [data-form-status]').forEach((node) => {
        node.removeAttribute('aria-invalid');
        node.removeAttribute('aria-describedby');
        node.removeAttribute('autofocus');
        node.removeAttribute('data-form-status');
      });
      form.querySelectorAll('.input-group--error').forEach((node) => node.classList.remove('input-group--error'));
      form.querySelectorAll('.input--error').forEach((node) => node.classList.remove('input--error'));
      form.querySelectorAll('.field-wrapper__icon-error').forEach((node) => node.classList.add('hide'));
      form.querySelectorAll('input[name="contact[email]"]').forEach((input) => {
        input.value = '';
      });
    });
  };

  const focusCustomerFormFeedback = () => {
    const form = document.querySelector('form.sb-customer-form[data-sb-feedback-target="true"]');
    if (
      !form ||
      !form.querySelector('[data-sb-customer-posted-error]') ||
      form.dataset.sbFeedbackFocused === 'true'
    ) {
      return;
    }

    const target = form.querySelector('[aria-invalid="true"], [data-sb-customer-error]');
    if (!target) return;

    form.dataset.sbFeedbackFocused = 'true';
    target.focus();
  };

  const publishConfirmedFormSuccesses = () => {
    document.querySelectorAll('[data-sb-success-event]').forEach((node) => {
      const customerForm = node.closest('form.sb-customer-form');
      if (customerForm && customerForm.dataset.sbFeedbackTarget !== 'true') return;

      const eventName = node.dataset.sbSuccessEvent;
      const formId = node.dataset.sbEventFormId || 'form';
      const dedupeKey = `silverBulletEvent:${eventName}:${formId}:${window.location.pathname}:${window.location.search}`;

      try {
        if (window.sessionStorage.getItem(dedupeKey) === 'published') return;
        window.sessionStorage.setItem(dedupeKey, 'published');
      } catch (error) {
        if (node.dataset.sbEventPublished === 'true') return;
        node.dataset.sbEventPublished = 'true';
      }

      publish(eventName, {
        source: node.dataset.sbEventSource || 'unknown',
        page_type: node.dataset.sbEventPageType || 'unknown',
      });
    });
  };

  const revealRelevantDisclosures = () => {
    document
      .querySelectorAll(
        '[data-sb-disclosure-reveal] [aria-invalid="true"], [data-sb-disclosure-reveal] [data-sb-success-event], [data-sb-disclosure-reveal] .errors'
      )
      .forEach((node) => {
        const customerForm = node.closest('form.sb-customer-form');
        if (customerForm && customerForm.dataset.sbFeedbackTarget !== 'true') return;

        const disclosure = node.closest('details[data-sb-disclosure-reveal]');
        if (disclosure) disclosure.open = true;
      });

    if (!window.location.hash) return;

    try {
      const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (target instanceof HTMLDetailsElement) target.open = true;
    } catch (error) {
      // A malformed fragment must not interrupt forms or navigation.
    }
  };

  const initialize = () => {
    reconcileCustomerFormFeedback();
    revealRelevantDisclosures();
    focusCustomerFormFeedback();
    publishConfirmedFormSuccesses();
  };

  window.SilverBulletEvents = { publish };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('hashchange', revealRelevantDisclosures);
})();
